import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  MapPin, Music, Ticket, UtensilsCrossed, Waves, Mountain, Star,
  Plus, Pencil, Trash2, ExternalLink, X, Check, SlidersHorizontal,
  MoreHorizontal, ChevronDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import AddToScenarioDialog from "./AddToScenarioDialog";

const inputCls = "w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm focus:outline-none focus:border-ember transition-colors";

const TYPES = [
  { value: "place", label: "Place", Icon: MapPin },
  { value: "concert", label: "Concert", Icon: Music },
  { value: "event", label: "Event", Icon: Ticket },
  { value: "restaurant", label: "Restaurant", Icon: UtensilsCrossed },
  { value: "dive_site", label: "Dive site", Icon: Waves },
  { value: "activity", label: "Activity", Icon: Mountain },
  { value: "other", label: "Other", Icon: Star },
];
const typeIcon = (t) => (TYPES.find((x) => x.value === t) || TYPES[6]).Icon;

const TYPE_TINT = {
  concert: "bg-ember-soft text-ember",
  event: "bg-ember-soft text-ember",
  place: "bg-dusk-soft text-dusk",
  restaurant: "bg-dusk-soft text-dusk",
  dive_site: "bg-dusk-soft text-dusk",
  activity: "bg-hour-soft text-hour-deep",
  other: "bg-hour-soft text-hour-deep",
};

const STATUS_CHIP = {
  active: { label: "◇ ACTIVE", cls: "bg-sand-100 text-sand-600" },
  planned: { label: "◷ PLANNED", cls: "bg-hour-soft text-hour-deep" },
  done: { label: "✓ DONE", cls: "bg-success-soft text-success" },
};

const PRIORITIES = [
  { value: "must_do", label: "Must do" },
  { value: "high", label: "High" },
  { value: "someday", label: "Someday" },
];

const EMPTY = {
  title: "", type: "place", location: "", city: "", country: "",
  date_from: "", date_to: "", season: "", priority: "high", notes: "", url: "",
};

function whenLabel(it) {
  if (it.season) return it.season;
  if (it.date_from && it.date_to) return `${it.date_from} – ${it.date_to}`;
  if (it.date_from) return it.date_from;
  return "anytime";
}

function ItemForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState(() => {
    const merged = { ...EMPTY, ...initial };
    Object.keys(merged).forEach((k) => { if (merged[k] === null) merged[k] = ""; });
    return merged;
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const save = async () => {
    if (!f.title.trim()) { toast.error("Give it a title"); return; }
    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(), type: f.type, location: f.location, city: f.city, country: f.country,
        date_from: f.date_from || null, date_to: f.date_to || null, season: f.season,
        priority: f.priority, notes: f.notes, url: f.url,
      };
      if (initial?.id) await api.patch(`/wishlist/${initial.id}`, payload);
      else await api.post("/wishlist", payload);
      toast.success(initial?.id ? "Updated" : "Added to wishlist");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface border border-ember p-5 md:p-6 space-y-4" data-testid="wishlist-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="label-tag block mb-2">Title</label>
          <input className={inputCls} value={f.title} onChange={set("title")} placeholder="e.g. Phish at MSG" data-testid="wishlist-title-input" />
        </div>
        <div>
          <label className="label-tag block mb-2">Type</label>
          <select className={`${inputCls} font-mono`} value={f.type} onChange={set("type")} data-testid="wishlist-type-select">
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label-tag block mb-2">Location / venue</label>
          <input className={inputCls} value={f.location} onChange={set("location")} placeholder="Madison Square Garden" data-testid="wishlist-location-input" />
        </div>
        <div>
          <label className="label-tag block mb-2">City</label>
          <input className={inputCls} value={f.city} onChange={set("city")} placeholder="New York" data-testid="wishlist-city-input" />
        </div>
        <div>
          <label className="label-tag block mb-2">Country</label>
          <input className={inputCls} value={f.country} onChange={set("country")} placeholder="US" data-testid="wishlist-country-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="label-tag block mb-2">Date from</label>
          <input type="date" className={`${inputCls} font-mono`} value={f.date_from} onChange={set("date_from")} data-testid="wishlist-date-from" />
        </div>
        <div>
          <label className="label-tag block mb-2">Date to</label>
          <input type="date" className={`${inputCls} font-mono`} value={f.date_to} onChange={set("date_to")} data-testid="wishlist-date-to" />
        </div>
        <div>
          <label className="label-tag block mb-2">Or season</label>
          <input className={inputCls} value={f.season} onChange={set("season")} placeholder="Sep–Nov" data-testid="wishlist-season-input" />
        </div>
        <div>
          <label className="label-tag block mb-2">Priority</label>
          <select className={`${inputCls} font-mono`} value={f.priority} onChange={set("priority")} data-testid="wishlist-priority-select">
            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-tag block mb-2">Notes</label>
          <input className={inputCls} value={f.notes} onChange={set("notes")} placeholder="Anything the AI should know" data-testid="wishlist-notes-input" />
        </div>
        <div>
          <label className="label-tag block mb-2">URL</label>
          <input className={inputCls} value={f.url} onChange={set("url")} placeholder="https://…" data-testid="wishlist-url-input" />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="btn-swiss btn-ghost text-sm" data-testid="wishlist-form-cancel">
          <X size={14} className="mr-1.5" /> Cancel
        </button>
        <button onClick={save} disabled={saving} className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid="wishlist-form-save">
          <Check size={14} className="mr-1.5" /> {saving ? "Saving…" : initial?.id ? "Save changes" : "Add to wishlist"}
        </button>
      </div>
    </div>
  );
}

function ClampedNote({ text }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 90;
  return (
    <div className="mt-2.5">
      <p className={`text-[13px] leading-relaxed text-sand-600 ${open ? "" : "line-clamp-2"}`}>{text}</p>
      {long && (
        <button onClick={() => setOpen((v) => !v)} className="text-xs font-semibold text-ember mt-0.5">
          {open ? "Less" : "More"}
        </button>
      )}
    </div>
  );
}

function ItemRow({ item, onEdit, onChanged, onScenario }) {
  const Icon = typeIcon(item.type);
  const done = item.status === "done";
  const chip = STATUS_CHIP[item.status] || STATUS_CHIP.active;
  const tint = TYPE_TINT[item.type] || TYPE_TINT.other;
  const meta = [
    [item.location, item.city, item.country].filter(Boolean).join(", "),
    whenLabel(item),
  ].filter(Boolean).join(" · ");

  const setStatus = async (status) => {
    try {
      await api.patch(`/wishlist/${item.id}`, { status });
      onChanged();
    } catch { toast.error("Could not update status"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/wishlist/${item.id}`);
      toast.success("Removed");
      onChanged();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <>
      {/* mobile card */}
      <div className={`sm:hidden bg-surface rounded-card border border-hairline p-4 ${done ? "opacity-60" : ""}`} data-testid={`wishlist-card-${item.id}`}>
        <div className="flex items-start gap-2.5">
          <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 ${tint}`}>
            <Icon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-display text-lg leading-snug ${done ? "line-through" : ""}`}>{item.title}</p>
            {meta && (
              <p className="font-mono text-[10px] uppercase tracking-wide text-sand-400 mt-1">{meta}</p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-8 h-8 -mr-1.5 -mt-1 flex items-center justify-center text-sand-400 hover:text-ink shrink-0" data-testid={`wishlist-menu-${item.id}`}>
                <MoreHorizontal size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider text-sand-400">Status</DropdownMenuLabel>
              {["active", "planned", "done"].map((s) => (
                <DropdownMenuItem key={s} onClick={() => setStatus(s)} className="capitalize" data-testid={`wishlist-menu-status-${s}-${item.id}`}>
                  <span className="w-4">{item.status === s ? "✓" : ""}</span>{s}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onScenario} data-testid={`wishlist-menu-scenario-${item.id}`}>Add to scenario</DropdownMenuItem>
              <DropdownMenuItem onClick={onEdit} data-testid={`wishlist-menu-edit-${item.id}`}>Edit</DropdownMenuItem>
              {item.url && (
                <DropdownMenuItem asChild>
                  <a href={item.url} target="_blank" rel="noreferrer">Open link ↗</a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={remove} className="text-red-600 focus:text-red-600" data-testid={`wishlist-menu-delete-${item.id}`}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <span className={`font-mono text-[10px] rounded-full px-2.5 py-1 ${chip.cls}`}>{chip.label}</span>
          {item.planned_trip_id && (
            <Link to={`/trips/${item.planned_trip_id}`} className="font-mono text-[10px] rounded-full px-2.5 py-1 bg-dusk-soft text-dusk" data-testid={`wishlist-trip-chip-${item.id}`}>
              → ON A TRIP
            </Link>
          )}
        </div>

        {item.notes && <ClampedNote text={item.notes} />}

        {item.scenario_est?.cost != null && (
          <Link to="/plan/scenarios" className="mt-3 flex bg-paper rounded-xl overflow-hidden" data-testid={`wishlist-est-m-${item.id}`}>
            <div className="flex-1 py-2.5 px-2 text-center border-r border-hairline">
              <p className="font-mono text-sm text-ember">${Number(item.scenario_est.cost).toLocaleString()}</p>
              <p className="font-mono text-[8px] tracking-wider text-sand-400 mt-0.5">FULLY-LOADED EST</p>
            </div>
            <div className="flex-1 py-2.5 px-2 text-center min-w-0">
              <p className="font-mono text-sm text-ink truncate">{item.scenario_est.scenario_name || "Scenario"}</p>
              <p className="font-mono text-[8px] tracking-wider text-sand-400 mt-0.5">FROM SCENARIO</p>
            </div>
          </Link>
        )}
      </div>

      {/* desktop row */}
      <div className={`hidden sm:flex bg-surface rounded-card border border-hairline p-4 items-start gap-3 ${done ? "opacity-50" : ""}`} data-testid={`wishlist-item-${item.id}`}>
        <Icon size={16} className="mt-0.5 text-ember shrink-0" />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${done ? "line-through" : ""}`}>
            {item.title}
            {item.url && (
              <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex ml-1.5 text-sand-400 hover:text-ember align-middle">
                <ExternalLink size={12} />
              </a>
            )}
          </p>
          <p className="text-xs text-sand-500 mt-0.5 font-mono">
            {meta}
            {item.planned_trip_id && (
              <Link to={`/trips/${item.planned_trip_id}`} className="ml-2 text-ember hover:underline" data-testid={`wishlist-trip-link-${item.id}`}>
                → on a trip
              </Link>
            )}
          </p>
          {item.notes && <p className="text-xs text-sand-500 mt-1">{item.notes}</p>}
          {item.scenario_est?.cost != null && (
            <Link
              to="/plan/scenarios"
              className="inline-flex items-center gap-1 mt-1.5 font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 border border-ember/40 text-ember hover:bg-ember/5 transition-colors"
              title={`From scenario "${item.scenario_est.scenario_name}"${item.scenario_est.unpriced_items ? ` · ${item.scenario_est.unpriced_items} unpriced item(s)` : ""}`}
              data-testid={`wishlist-est-${item.id}`}
            >
              <SlidersHorizontal size={10} />
              fully-loaded est ${Number(item.scenario_est.cost).toLocaleString()}
            </Link>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <select value={item.status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-card border border-hairline bg-surface px-1.5 py-1 text-[11px] font-mono text-sand-600"
            data-testid={`wishlist-status-${item.id}`}>
            <option value="active">Active</option>
            <option value="planned">Planned</option>
            <option value="done">Done</option>
          </select>
          <button onClick={onScenario} className="p-1.5 text-sand-400 hover:text-ember" title="Add to scenario — see the fully-loaded cost" data-testid={`wishlist-scenario-${item.id}`}>
            <SlidersHorizontal size={13} />
          </button>
          <button onClick={onEdit} className="p-1.5 text-sand-400 hover:text-ember" data-testid={`wishlist-edit-${item.id}`}>
            <Pencil size={13} />
          </button>
          <button onClick={remove} className="p-1.5 text-sand-400 hover:text-red-600" data-testid={`wishlist-delete-${item.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </>
  );
}

const GROUPS = [
  { key: "must_do", label: "Must do", dot: "bg-ember" },
  { key: "high", label: "High", dot: "bg-hour" },
  { key: "someday", label: "Someday", dot: "bg-dusk" },
];

export default function WishlistTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [scenarioFor, setScenarioFor] = useState(null);
  const [prioFilter, setPrioFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/wishlist");
      setItems(data);
    } catch {
      toast.error("Could not load wishlist");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const open = (items || []).filter((i) => i.status !== "done");
  const doneItems = (items || []).filter((i) => i.status === "done");
  const groups = GROUPS.filter((g) => prioFilter === "all" || g.key === prioFilter);

  return (
    <div data-testid="wishlist-tab">
      {/* desktop intro */}
      <div className="hidden sm:flex items-start justify-between gap-4 mb-6 flex-wrap">
        <p className="text-sm text-sand-600 max-w-xl">
          Places, concerts, and experiences you want. The AI planner reads this list and surfaces
          items when a trip puts you nearby or in the right window.
        </p>
        {editing === null && (
          <button onClick={() => setEditing("new")} className="btn-swiss btn-primary text-sm shrink-0" data-testid="wishlist-add-btn">
            <Plus size={15} className="mr-2" /> Add item
          </button>
        )}
      </div>

      {/* mobile intro: tight copy + pill add + priority filter */}
      <div className="sm:hidden mb-5">
        <p className="text-[13px] leading-snug text-sand-600">
          Places & experiences you want. The AI planner surfaces these when a trip puts you nearby.
        </p>
        <div className="mt-3.5 flex items-center justify-between gap-3">
          {editing === null ? (
            <button
              onClick={() => setEditing("new")}
              className="inline-flex items-center gap-1.5 rounded-full bg-ember text-cream text-sm font-semibold px-4 py-2.5 shadow-raised active:scale-95 transition-transform"
              data-testid="wishlist-add-btn-m"
            >
              <Plus size={15} /> Add item
            </button>
          ) : <span />}
          <div className="relative shrink-0">
            <select
              value={prioFilter}
              onChange={(e) => setPrioFilter(e.target.value)}
              className="appearance-none font-mono text-[11px] uppercase tracking-wide text-sand-600 border border-hairline rounded-full bg-surface pl-3.5 pr-8 py-2 focus:outline-none focus:border-ember"
              data-testid="wishlist-priority-filter"
            >
              <option value="all">All</option>
              <option value="must_do">Must do</option>
              <option value="high">High</option>
              <option value="someday">Someday</option>
            </select>
            <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-sand-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {editing !== null && (
        <div className="mb-8">
          <ItemForm
            initial={editing === "new" ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        </div>
      )}

      {items === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="rounded-card border border-hairline bg-surface h-16 animate-pulse" />)}</div>
      ) : items.length === 0 && editing === null ? (
        <div className="rounded-card border border-hairline bg-surface p-10 text-center" data-testid="wishlist-empty">
          <p className="text-sm text-sand-600 mb-4">Nothing on the list yet.</p>
          <button onClick={() => setEditing("new")} className="btn-swiss btn-outline text-sm">
            <Plus size={14} className="mr-2" /> Add your first wish
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => {
            const group = open.filter((i) => i.priority === g.key);
            if (group.length === 0) return null;
            return (
              <div key={g.key} data-testid={`wishlist-group-${g.key}`}>
                <p className="label-tag mb-3 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${g.dot}`} />
                  {g.label} · {group.length}
                </p>
                <div className="space-y-2">
                  {group.map((it) => (
                    <ItemRow key={it.id} item={it} onEdit={() => setEditing(it)} onChanged={load} onScenario={() => setScenarioFor(it)} />
                  ))}
                </div>
              </div>
            );
          })}
          {doneItems.length > 0 && prioFilter === "all" && (
            <div data-testid="wishlist-group-done">
              <p className="label-tag mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-sand-300" />
                Done · {doneItems.length}
              </p>
              <div className="space-y-2">
                {doneItems.map((it) => (
                  <ItemRow key={it.id} item={it} onEdit={() => setEditing(it)} onChanged={load} onScenario={() => setScenarioFor(it)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {scenarioFor && <AddToScenarioDialog item={scenarioFor} onClose={() => setScenarioFor(null)} />}
    </div>
  );
}
